'use client';

import { useEffect, useRef, type ComponentPropsWithoutRef } from 'react';
import { useInView, useMotionValue, useSpring } from 'motion/react';

import { cn } from '@/lib/utils';

interface NumberTickerProps extends ComponentPropsWithoutRef<'span'> {
  value: number;
  startValue?: number;
  direction?: 'up' | 'down';
  delay?: number;
  decimalPlaces?: number;
  /**
   * R5-P3 [ADV-18], additive: an external start gate. `undefined` (default)
   * preserves the ORIGINAL self-contained behavior byte-for-byte — this
   * component observes its own viewport entry via `useInView({ once: true })`
   * exactly as before (every existing consumer — `BigStat`, `StatTile`,
   * `InsightsSummaryCards` — passes no such prop and is therefore
   * unaffected). When a caller passes an explicit `true`/`false`, THAT value
   * decides instead of self-observation — for a parent that orchestrates
   * several tickers to start together on its OWN, differently-tuned trigger
   * (see `BigStatsMotion`'s stats-row dwell trigger). The caller is
   * responsible for only ever flipping it `false` → `true` once (this
   * component does not re-arm on a later `false`, matching the `once: true`
   * semantics of the self-observed path it replaces).
   */
  startSignal?: boolean;
}

export function NumberTicker({
  value,
  startValue = 0,
  direction = 'up',
  delay = 0,
  className,
  decimalPlaces = 0,
  startSignal,
  ...props
}: NumberTickerProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(direction === 'down' ? value : startValue);
  const springValue = useSpring(motionValue, {
    damping: 60,
    stiffness: 100,
  });
  const selfInView = useInView(ref, { once: true, margin: '0px' });
  // `??` (not `||`): an explicit `false` from a parent-controlled caller must
  // stay `false` ("not yet") rather than falling back to self-observation.
  const isInView = startSignal ?? selfInView;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (isInView) {
      timer = setTimeout(() => {
        motionValue.set(direction === 'down' ? startValue : value);
      }, delay * 1000);
    }

    return () => {
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [motionValue, isInView, delay, value, direction, startValue]);

  useEffect(
    () =>
      springValue.on('change', (latest) => {
        if (ref.current) {
          ref.current.textContent = Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          }).format(Number(latest.toFixed(decimalPlaces)));
        }
      }),
    [springValue, decimalPlaces]
  );

  return (
    <span
      ref={ref}
      className={cn(
        'inline-block tracking-wider text-black tabular-nums dark:text-white',
        className
      )}
      {...props}
    >
      {startValue}
    </span>
  );
}
